import {
  EVENT_CAPTURE_TIMEFRAMES,
  EVENT_EVALUATION_TIMEFRAMES,
  INSIGHTS_EVENT_TYPES,
} from "../config/insightsEventTypes";
import { closeAdminCommandTransport, recoverCommandStatuses, routeJavaCommand, waitForLegacyCommand } from "../commands/commandTransport";

const ADMIN_BASE_PATH = "/insights/admin";
function validWatchlistConfiguration(payload) {
  const config = payload?.configuration;
  return Number.isSafeInteger(payload?.revision) && payload.revision >= 0
    && config && config.metadata && typeof config.metadata === "object" && !Array.isArray(config.metadata)
    && ["maxMarketCap", "minQuoteVolume5m", "minRvol", "extendedPercent"]
      .every(key => Number.isFinite(config.settings?.[key]) && config.settings[key] > 0);
}
const AUTH_BASE_PATH = `${ADMIN_BASE_PATH}/auth`;
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);
const DOCUMENT_KEYS = Object.freeze(["customAi", "codex", "eventEvaluation"]);
const CUSTOM_AI_MODELS = new Set([
  "gpt-5-nano", "gpt-5.4-mini", "gpt-5.6-luna", "gpt-6-luna", "gpt-6-sol",
]);
const CODEX_MODELS = new Set([
  "gpt-6-astra", "gpt-6-sol", "gpt-6-luna",
  "gpt-5.6-sol", "gpt-5.6-terra", "gpt-5.6-luna", "gpt-5.5", "gpt-5.4", "gpt-5.4-mini",
]);
const TARGET_TYPES = new Set(["SETUP", "MOVE_STOP"]);
const REASONING_EFFORTS = new Set(["none", "low", "medium", "high", "xhigh", "max"]);
const EVENT_TYPES = Object.freeze(INSIGHTS_EVENT_TYPES.map(({ value }) => value));
const EVENT_MODES = new Set(["OFF", "LIGHT", "REFERENCE", "STANDARD"]);
const EVENT_TIMEFRAME_MAP = EVENT_CAPTURE_TIMEFRAMES;
const EVENT_PROMPT_CONTROL_CHARACTERS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/;
const SOURCES = new Set(["DEFAULT", "INSIGHTS", "TELEGRAM", "SYSTEM"]);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const INVALID_RESPONSE_MESSAGE = "The admin service returned invalid configuration data. Refresh and try again.";
const HEALTH_STATES = new Set(["HEALTHY", "UNKNOWN", "DEGRADED", "FAILED"]);
const REFERENCE_BASE_PATH = `${ADMIN_BASE_PATH}/reference-packs`;
const REFERENCE_SLOTS = Object.freeze(["S1", "S2", "S3", "F1", "F2", "F3"]);
const REFERENCE_ID = /^[a-f0-9]{64}$/;
const REFERENCE_METADATA = Object.freeze({ sourceSymbol: 80, eventDate: 10, outcome: 200, mfe: 80, mae: 80, note: 2000 });
const REFERENCE_CAPTURE_FIELDS = Object.freeze(["outcomeImageId", "decisionTime", "capturePolicy"]);

function validReferenceCapture(slot) {
  const hasField = (field) => Object.prototype.hasOwnProperty.call(slot, field);
  if (!REFERENCE_CAPTURE_FIELDS.some(hasField)) return true;
  if (!REFERENCE_CAPTURE_FIELDS.every(hasField)
      || typeof slot.outcomeImageId !== "string" || !REFERENCE_ID.test(slot.outcomeImageId)
      || !["closed-candles-v1", "as-of-htf-v2"].includes(slot.capturePolicy)
      || typeof slot.decisionTime !== "string"
      || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(slot.decisionTime)) return false;
  const timestamp = Date.parse(slot.decisionTime);
  return Number.isSafeInteger(timestamp) && timestamp >= 0
    && new Date(timestamp).toISOString() === slot.decisionTime;
}

function validReferenceSlots(slots) {
  if (!isPlainObject(slots) || Object.keys(slots).some((key) => !REFERENCE_SLOTS.includes(key))) return false;
  return Object.values(slots).every((slot) => slot === null || (isPlainObject(slot)
    && typeof slot.imageId === "string" && REFERENCE_ID.test(slot.imageId)
    && Object.keys(slot).every((key) => key === "imageId" || REFERENCE_CAPTURE_FIELDS.includes(key)
      || Object.prototype.hasOwnProperty.call(REFERENCE_METADATA, key))
    && validReferenceCapture(slot)
    && Object.entries(REFERENCE_METADATA).every(([key, maximum]) => slot[key] === undefined
      || (typeof slot[key] === "string" && slot[key].length <= maximum))
    && ["mfe", "mae"].every((key) => !slot[key]
      || (/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(slot[key]) && Number.isFinite(Number(slot[key]))))
    && (!slot.eventDate || (/^\d{4}-\d{2}-\d{2}$/.test(slot.eventDate)
      && !Number.isNaN(Date.parse(slot.eventDate))
      && new Date(slot.eventDate).toISOString().slice(0, 10) === slot.eventDate))));
}

function validReferencePack(pack) {
  const timestamp = (value) => (Number.isSafeInteger(value) && value >= 0) || isTimestamp(value);
  if (!isPlainObject(pack) || !REFERENCE_ID.test(pack.id)
      || !EVENT_TYPES.includes(pack.eventType) || !["LONG", "SHORT"].includes(pack.direction)
      || !EVENT_EVALUATION_TIMEFRAMES.includes(pack.timeframe)
      || !isBoundedInteger(pack.revision, 1) || typeof pack.enabled !== "boolean"
      || !Array.isArray(pack.versions) || pack.versions.length === 0
      || !Array.isArray(pack.retiredVersions) || !Array.isArray(pack.history)) return false;
  if (!pack.versions.every((entry) => isPlainObject(entry) && isBoundedInteger(entry.version, 1)
      && timestamp(entry.createdAt) && typeof entry.createdBy === "string"
      && entry.createdBy.length <= 100 && validReferenceSlots(entry.slots))) return false;
  const versionIds = new Set(pack.versions.map((entry) => entry.version));
  const active = pack.versions.find((entry) => entry.version === pack.activeVersion);
  return versionIds.size === pack.versions.length
    && new Set(pack.retiredVersions).size === pack.retiredVersions.length
    && pack.retiredVersions.every((version) => versionIds.has(version))
    && (pack.activeVersion === null || (active && !pack.retiredVersions.includes(pack.activeVersion)
      && REFERENCE_SLOTS.every((slot) => !!active.slots[slot]?.imageId)))
    && (!pack.enabled || !!active)
    && pack.history.every((entry) => isPlainObject(entry)
      && typeof entry.action === "string" && entry.action.length > 0 && entry.action.length <= 100
      && timestamp(entry.at) && typeof entry.actor === "string" && entry.actor.length <= 100
      && (entry.version === null || versionIds.has(entry.version)));
}

function validReferenceImage(value) {
  return isPlainObject(value) && REFERENCE_ID.test(value.imageId) && value.contentType === "image/png"
    && isBoundedInteger(value.width, 1, 4096) && isBoundedInteger(value.height, 1, 4096)
    && value.width * value.height <= 4_000_000 && isBoundedInteger(value.bytes, 1, 16 * 1024 * 1024);
}

const ERROR_CONTRACTS = Object.freeze({
  400: ["INVALID_REQUEST", "The admin request was not accepted."],
  401: ["SESSION_EXPIRED", "Your admin session has expired. Please sign in again."],
  403: ["REQUEST_FORBIDDEN", "The admin request could not be verified. Refresh the page and try again."],
  409: ["REVISION_CONFLICT", "This configuration changed on the server. Review the latest revision before saving."],
  429: ["TOO_MANY_ATTEMPTS", "Too many sign-in attempts. Please wait and try again."],
  503: ["TEMPORARILY_UNAVAILABLE", "The admin service is temporarily unavailable. Please try again."],
});

export class AdminApiError extends Error {
  constructor(status, code, message, options = {}) {
    super(message);
    this.name = "AdminApiError";
    this.status = status;
    this.code = code;
    if (options.staleAuthResponse) this.staleAuthResponse = true;
    if (options.requiresFreshLoad) this.requiresFreshLoad = true;
    if (status === 409 && options.current) this.current = options.current;
  }

  toJSON() {
    return { name: this.name, status: this.status, code: this.code, message: this.message };
  }
}

function isPlainObject(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasExactKeys(value, keys) {
  return isPlainObject(value)
    && Object.keys(value).length === keys.length
    && keys.every((key) => Object.prototype.hasOwnProperty.call(value, key));
}

function isBoundedInteger(value, minimum, maximum = 2_147_483_647) {
  return Number.isSafeInteger(value) && value >= minimum && value <= maximum;
}

function isRevision(value) {
  return Number.isSafeInteger(value) && value >= 0;
}

function isTimestamp(value) {
  return typeof value === "string" && value.length > 0 && !Number.isNaN(Date.parse(value));
}

function isSyncTimestamp(value) {
  return value === null
    || isTimestamp(value)
    || (Number.isSafeInteger(value) && value >= 0);
}

function isHealthTimestamp(value) {
  return value === null || (Number.isSafeInteger(value) && value >= 0);
}

function validHealthComponent(value) {
  return hasExactKeys(value, ["state", "lastHeartbeatAt", "lastSuccessAt", "lastError", "restartAttempts"])
    && HEALTH_STATES.has(value.state)
    && isHealthTimestamp(value.lastHeartbeatAt)
    && isHealthTimestamp(value.lastSuccessAt)
    && (value.lastError === null || (typeof value.lastError === "string" && value.lastError.length <= 500))
    && isBoundedInteger(value.restartAttempts, 0, 100);
}

function validHealth(value) {
  const componentKeys = ["java", "capture", "webhook", "evaluator"];
  const countKeys = ["pendingCapture", "captureLeased", "pending", "leased", "completed", "failed"];
  const queueKeys = [
    "counts", "oldestCaptureAgeMs", "oldestEvaluationAgeMs", "lastCompletedAt",
    "lastFailedAt", "lastFailure", "captureWorkerLastSeenAt", "state",
  ];
  // Health responses may add metadata as the fleet evolves. Validate the fields
  // this UI consumes while retaining compatibility with older webhook releases.
  const hasFields = (object, fields) => isPlainObject(object)
    && fields.every((key) => Object.prototype.hasOwnProperty.call(object, key));
  return hasFields(value, ["overall", "generatedAt", "components", "queue"])
    && HEALTH_STATES.has(value.overall)
    && isHealthTimestamp(value.generatedAt)
    && hasExactKeys(value.components, componentKeys)
    && componentKeys.every((key) => validHealthComponent(value.components[key]))
    && hasFields(value.queue, queueKeys)
    && HEALTH_STATES.has(value.queue.state)
    && hasExactKeys(value.queue.counts, countKeys)
    && countKeys.every((key) => isBoundedInteger(value.queue.counts[key], 0))
    && isBoundedInteger(value.queue.oldestCaptureAgeMs, 0)
    && isBoundedInteger(value.queue.oldestEvaluationAgeMs, 0)
    && isHealthTimestamp(value.queue.lastCompletedAt)
    && isHealthTimestamp(value.queue.lastFailedAt)
    && isHealthTimestamp(value.queue.captureWorkerLastSeenAt)
    && (value.queue.lastFailure === null
      || (typeof value.queue.lastFailure === "string" && value.queue.lastFailure.length <= 500))
    && (value.workerActivity === undefined || validWorkerActivity(value.workerActivity));
}

function validWorkerActivity(value) {
  if (!hasExactKeys(value, ["windowMs", "activeWorkers", "requestingWorkers", "processingWorkers", "workers"])
      || !isBoundedInteger(value.windowMs, 1)
      || !Array.isArray(value.workers) || value.workers.length > 128) return false;
  const workers = value.workers;
  return workers.every((worker) => hasExactKeys(worker,
    ["workerId", "ip", "lastClaimRequestAt", "activeLeases", "requesting", "processing"])
    && typeof worker.workerId === "string" && /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/.test(worker.workerId)
    && (worker.ip === null || (typeof worker.ip === "string" && worker.ip.length <= 128))
    && isHealthTimestamp(worker.lastClaimRequestAt)
    && isBoundedInteger(worker.activeLeases, 0)
    && typeof worker.requesting === "boolean" && typeof worker.processing === "boolean")
    && new Set(workers.map((worker) => worker.workerId)).size === workers.length
    && value.activeWorkers === workers.filter((worker) => worker.requesting || worker.processing).length
    && value.requestingWorkers === workers.filter((worker) => worker.requesting).length
    && value.processingWorkers === workers.filter((worker) => worker.processing).length;
}

function validWorkerPool(value) {
  return hasExactKeys(value, ["concurrency", "revision", "updatedAt"])
    && isBoundedInteger(value.concurrency, 1, 8)
    && isRevision(value.revision)
    && Number.isSafeInteger(value.updatedAt) && value.updatedAt >= 0;
}

function validConfiguration(key, value) {
  if (key === "customAi") {
    return hasExactKeys(value, ["enabled", "model", "dailyLimit"])
      && typeof value.enabled === "boolean"
      && CUSTOM_AI_MODELS.has(value.model)
      && isBoundedInteger(value.dailyLimit, 0, 10_000);
  }
  if (key === "codex") {
    if (!hasExactKeys(value, [
      "enabled", "enabledTargetTypes", "screenshotsEnabled", "automaticModel", "manualModel",
      "reasoningEffort", "timeoutSeconds", "concurrency", "eventContextLimit",
      "automaticDailyLimit", "manualDailyLimit",
    ])) return false;
    const model = (candidate) => typeof candidate === "string"
      && candidate.length <= 100 && candidate.length > 0 && candidate.trim() === candidate;
    return typeof value.enabled === "boolean"
      && typeof value.screenshotsEnabled === "boolean"
      && Array.isArray(value.enabledTargetTypes)
      && new Set(value.enabledTargetTypes).size === value.enabledTargetTypes.length
      && value.enabledTargetTypes.every((target) => TARGET_TYPES.has(target))
      && model(value.automaticModel) && model(value.manualModel)
      && REASONING_EFFORTS.has(value.reasoningEffort)
      && isBoundedInteger(value.timeoutSeconds, 60, 900)
      && isBoundedInteger(value.concurrency, 1, 8)
      && isBoundedInteger(value.eventContextLimit, 0, 10)
      && isBoundedInteger(value.automaticDailyLimit, 0)
      && isBoundedInteger(value.manualDailyLimit, 0);
  }
  if (key === "eventEvaluation") {
    const fields = ["eventPolicies", "light", "reference", "timeframeMap"];
    if (!hasExactKeys(value, fields)
        || !hasExactKeys(value.eventPolicies, EVENT_TYPES)) return false;
    const validPolicy = (policy) => hasExactKeys(policy, ["mode", "timeframes", "promptAppend"])
      && EVENT_MODES.has(policy.mode)
      && Array.isArray(policy.timeframes)
      && new Set(policy.timeframes).size === policy.timeframes.length
      && policy.timeframes.every((timeframe) => EVENT_EVALUATION_TIMEFRAMES.includes(timeframe))
      && (policy.mode === "OFF" || policy.timeframes.length > 0)
      && typeof policy.promptAppend === "string"
      && policy.promptAppend === policy.promptAppend.trim()
      && policy.promptAppend.length <= 500
      && !EVENT_PROMPT_CONTROL_CHARACTERS.test(policy.promptAppend);
    if (EVENT_TYPES.some((eventType) => !validPolicy(value.eventPolicies[eventType]))) return false;
    const validExecution = (section, extraFields = []) => hasExactKeys(section, [
      "model", "reasoningEffort", "timeoutSeconds", "concurrency", "dailyLimit", ...extraFields,
    ])
      && CODEX_MODELS.has(section.model)
      && REASONING_EFFORTS.has(section.reasoningEffort)
      && isBoundedInteger(section.timeoutSeconds, 30, 900)
      && isBoundedInteger(section.concurrency, 1, 8)
      && isBoundedInteger(section.dailyLimit, 0);
    if (!validExecution(value.light)
        || !validExecution(value.reference, ["successThreshold", "failureThreshold", "edgeThreshold"])
        || !isBoundedInteger(value.reference.successThreshold, 0, 100)
        || !isBoundedInteger(value.reference.failureThreshold, 0, 100)
        || !isBoundedInteger(value.reference.edgeThreshold, 0, 100)
        || value.reference.edgeThreshold > value.reference.successThreshold
        || value.reference.edgeThreshold > value.reference.failureThreshold
        || !hasExactKeys(value.timeframeMap, Object.keys(EVENT_TIMEFRAME_MAP))) return false;
    return Object.entries(EVENT_TIMEFRAME_MAP).every(([timeframe, expected]) => {
      const actual = value.timeframeMap[timeframe];
      return Array.isArray(actual)
        && actual.length === expected.length
        && actual.every((item, index) => item === expected[index]);
    });
  }
  return false;
}

function validEnvelope(key, value) {
  if (!hasExactKeys(value, ["revision", "savedAt", "savedByUserId", "source", "configuration"])
    || !isRevision(value.revision)
    || !isTimestamp(value.savedAt)
    || !SOURCES.has(value.source)
    || !validConfiguration(key, value.configuration)) return false;
  if (value.revision === 0) return value.savedByUserId === null && value.source === "DEFAULT";
  if (value.source === "SYSTEM") return value.savedByUserId === null;
  return typeof value.savedByUserId === "string" && UUID.test(value.savedByUserId)
    && (value.source === "INSIGHTS" || value.source === "TELEGRAM");
}

function validSyncEntry(value) {
  return hasExactKeys(value, [
    "desiredRevision", "javaAppliedRevision", "lastJavaPollAt", "lastJavaAckAt", "lastJavaError",
  ])
    && isRevision(value.desiredRevision)
    && isRevision(value.javaAppliedRevision)
    && isSyncTimestamp(value.lastJavaPollAt)
    && isSyncTimestamp(value.lastJavaAckAt)
    && (value.lastJavaError === null
      || (typeof value.lastJavaError === "string" && value.lastJavaError.length <= 500));
}

function validAuditEntry(value) {
  if (!isPlainObject(value)) return false;
  const allowed = new Set(["at", "action", "actorId", "address", "key", "revision", "source"]);
  if (Object.keys(value).some((key) => !allowed.has(key))
      || !Number.isFinite(value.at) || value.at < 0
      || typeof value.action !== "string" || value.action.length < 1 || value.action.length > 100) return false;
  if (value.actorId !== undefined && value.actorId !== null
      && (typeof value.actorId !== "string" || !UUID.test(value.actorId))) return false;
  if (value.address !== undefined
      && (typeof value.address !== "string" || value.address.length < 1 || value.address.length > 200)) return false;
  if (value.key !== undefined && !DOCUMENT_KEYS.includes(value.key)) return false;
  if (value.revision !== undefined && !isRevision(value.revision)) return false;
  return value.source === undefined || SOURCES.has(value.source);
}

function invalidResponse() {
  return new AdminApiError(0, "INVALID_ADMIN_RESPONSE", INVALID_RESPONSE_MESSAGE, {
    requiresFreshLoad: true,
  });
}

function requireValid(value, predicate) {
  if (!predicate(value)) throw invalidResponse();
  return value;
}

function publicSession(payload) {
  if (!payload || payload.authenticated !== true) return null;
  return {
    authenticated: true,
    publicUserId: String(payload.publicUserId || ""),
    displayLabel: String(payload.displayLabel || "Administrator"),
    lastSeenAt: payload.lastSeenAt,
  };
}

async function parseResponse(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (_) {
    return null;
  }
}

export function createAdminApi(options = {}) {
  const fetchImpl = options.fetchImpl || ((input, init) => fetch(input, init));
  let csrfToken = null;
  let authGeneration = 0;
  let currentSession = null;
  const expiryListeners = new Set();
  if (typeof options.onSessionExpired === "function") {
    expiryListeners.add(options.onSessionExpired);
  }

  const clearSession = (expectedGeneration, notify = true) => {
    if (expectedGeneration !== authGeneration) return false;
    authGeneration += 1;
    csrfToken = null;
    closeAdminCommandTransport();
    currentSession = null;
    if (notify) expiryListeners.forEach((listener) => listener());
    return true;
  };

  const establishSession = (payload) => {
    authGeneration += 1;
    csrfToken = typeof payload?.csrfToken === "string" ? payload.csrfToken : null;
    currentSession = publicSession(payload);
    if (currentSession) recoverCommandStatuses("ADMIN");
    return currentSession;
  };

  const request = async (path, requestOptions = {}) => {
    const requestGeneration = authGeneration;
    const method = String(requestOptions.method || "GET").toUpperCase();
    const headers = { Accept: "application/json", ...(requestOptions.headers || {}) };
    let body;
    if (requestOptions.body !== undefined) {
      headers["Content-Type"] = "application/json";
      body = typeof requestOptions.body === "string"
        ? requestOptions.body
        : JSON.stringify(requestOptions.body);
    }
    if (!SAFE_METHODS.has(method) && csrfToken) headers["X-CSRF-Token"] = csrfToken;

    let response;
    let payload;
    try {
      response = await fetchImpl(path, {
        credentials: "include",
        method,
        headers,
        ...(body === undefined ? {} : { body }),
      });
      payload = requestOptions.referenceImage && response.ok
        ? await response.blob() : await parseResponse(response);
    } catch (_) {
      throw new AdminApiError(
        0,
        "ADMIN_SERVICE_ERROR",
        "The admin service is temporarily unavailable.",
      );
    }
    // Private reference images and drafts must not cross an authentication change.
    if (requestOptions.referenceRequest && requestGeneration !== authGeneration) {
      throw new AdminApiError(401, "SESSION_CHANGED", "Your admin session changed. Sign in again.", {
        staleAuthResponse: true,
      });
    }
    if (!response.ok) {
      const loginFailure = response.status === 401 && requestOptions.loginRequest === true;
      const staleAuthResponse = response.status === 401 && requestGeneration !== authGeneration;
      const [code, message] = loginFailure
        ? ["INVALID_CREDENTIALS", "Invalid administrator password."]
        : (ERROR_CONTRACTS[response.status]
          || ["ADMIN_SERVICE_ERROR", "The admin service is temporarily unavailable."]);
      if (response.status === 401 && !loginFailure && !staleAuthResponse) {
        clearSession(requestGeneration);
      }
      let current;
      if (response.status === 409 && requestOptions.conflictKey) {
        if (!validEnvelope(requestOptions.conflictKey, payload?.current)) throw invalidResponse();
        current = payload.current;
      }
      if (response.status === 409 && requestOptions.workerPoolConflict) {
        if (!validWorkerPool(payload?.current)) throw invalidResponse();
        current = payload.current;
      }
      if (response.status === 409 && requestOptions.referenceConflict) {
        if (!validReferencePack(payload?.current)) throw invalidResponse();
        current = payload.current;
      }
      const referenceMessage = requestOptions.referenceRequest && [400, 413, 503].includes(response.status)
        && typeof payload?.error === "string" && payload.error.length <= 500 ? payload.error : message;
      throw new AdminApiError(response.status, code, referenceMessage, {
        current,
        staleAuthResponse,
      });
    }
    const transformed = requestOptions.transformResponse
      ? requestOptions.transformResponse(payload, requestGeneration)
      : payload;
    return requestOptions.validateResponse
      ? requireValid(transformed, requestOptions.validateResponse)
      : transformed;
  };

  return {
    async getSession() {
      try {
        return await request(`${AUTH_BASE_PATH}/session`, {
          transformResponse(payload, requestGeneration) {
            if (requestGeneration !== authGeneration) return currentSession;
            return establishSession(payload);
          },
        });
      } catch (error) {
        if (error?.status === 401 && error.staleAuthResponse) return currentSession;
        if (error?.status === 401) return null;
        throw error;
      }
    },

    async login(password) {
      const payload = await request(`${AUTH_BASE_PATH}/login`, {
        method: "POST",
        body: { password },
        loginRequest: true,
        transformResponse: establishSession,
      });
      return payload;
    },

    async logout() {
      try {
        return await request(`${AUTH_BASE_PATH}/logout`, {
          method: "POST",
          body: {},
          transformResponse(_payload, requestGeneration) {
            return clearSession(requestGeneration);
          },
        });
      } catch (error) {
        if (error?.status === 401 && error.staleAuthResponse) return false;
        throw error;
      }
    },

    subscribeSessionExpired(listener) {
      expiryListeners.add(listener);
      return () => expiryListeners.delete(listener);
    },

    getConfigurations() {
      return request(`${ADMIN_BASE_PATH}/configurations`, {
        validateResponse: (payload) => hasExactKeys(payload, DOCUMENT_KEYS)
          && DOCUMENT_KEYS.every((key) => validEnvelope(key, payload[key])),
      });
    },

    getConfiguration(key) {
      if (!DOCUMENT_KEYS.includes(key)) return Promise.reject(invalidResponse());
      return request(`${ADMIN_BASE_PATH}/configurations/${encodeURIComponent(key)}`, {
        validateResponse: (payload) => validEnvelope(key, payload),
      });
    },

    async saveConfiguration(key, configuration, expectedRevision) {
      if (!DOCUMENT_KEYS.includes(key) || !validConfiguration(key, configuration)
        || !isRevision(expectedRevision)) {
        return Promise.reject(new AdminApiError(
          0,
          "INVALID_ADMIN_CONFIGURATION",
          "The configuration contains an invalid value. Review every field and try again.",
        ));
      }
      const result = await routeJavaCommand({ type: "CONFIGURATION_UPDATE", payload: { key, configuration, expectedRevision } }, { scope: "ADMIN", legacy: () => request(`${ADMIN_BASE_PATH}/configurations/${encodeURIComponent(key)}`, {
        method: "PUT",
        body: { configuration, expectedRevision },
        conflictKey: key,
        validateResponse: (payload) => validEnvelope(key, payload),
      }) });
      if (!result.acknowledgement) return result;
      const ack = result.acknowledgement;
      if (!ack.accepted) throw new AdminApiError(ack.code === "REVISION_CONFLICT" ? 409 : 503, ack.code, ack.message,
        { current: ack.result?.current, requiresFreshLoad: true });
      return requireValid(ack.result?.document, value => validEnvelope(key, value));
    },

    getWorkerPool() {
      return request(`${ADMIN_BASE_PATH}/worker-pool`, { validateResponse: validWorkerPool });
    },

    getWatchlistConfiguration() {
      return request(`${ADMIN_BASE_PATH}/watchlist`, { validateResponse: validWatchlistConfiguration });
    },

    saveWatchlistConfiguration(configuration, expectedRevision) {
      return request(`${ADMIN_BASE_PATH}/watchlist`, {
        method: "PUT", body: { configuration, expectedRevision },
        validateResponse: validWatchlistConfiguration,
      });
    },

    saveWorkerPool(concurrency, expectedRevision) {
      if (!isBoundedInteger(concurrency, 1, 8) || !isRevision(expectedRevision)) {
        return Promise.reject(new AdminApiError(
          0,
          "INVALID_WORKER_POOL",
          "Maximum simultaneous assessments must be a whole number from 1 to 8.",
        ));
      }
      return request(`${ADMIN_BASE_PATH}/worker-pool`, {
        method: "PUT",
        body: { concurrency, expectedRevision },
        workerPoolConflict: true,
        validateResponse: validWorkerPool,
      });
    },

    getReferencePacks() {
      return request(REFERENCE_BASE_PATH, {
        referenceRequest: true,
        transformResponse: (payload) => payload?.packs,
        validateResponse: (packs) => Array.isArray(packs) && packs.every(validReferencePack)
          && new Set(packs.map((pack) => pack.id)).size === packs.length,
      });
    },

    uploadReferenceImage(dataBase64) {
      return request(`${REFERENCE_BASE_PATH}/images`, {
        method: "POST", body: { dataBase64 }, referenceRequest: true,
        validateResponse: validReferenceImage,
      });
    },

    getReferenceImage(imageId) {
      if (typeof imageId !== "string" || !REFERENCE_ID.test(imageId)) return Promise.reject(invalidResponse());
      // Never accept an image URL from a response: only a digest below the trusted admin base.
      return request(`${REFERENCE_BASE_PATH}/images/${encodeURIComponent(imageId)}`, {
        headers: { Accept: "image/png" }, referenceRequest: true, referenceImage: true,
        validateResponse: (blob) => blob?.type === "image/png" && blob.size > 0 && blob.size <= 16 * 1024 * 1024,
      });
    },

    saveReferencePackVersion(id, body) {
      if (!(id === "new" || (typeof id === "string" && REFERENCE_ID.test(id)))
          || !isRevision(body?.expectedRevision) || !EVENT_TYPES.includes(body.eventType)
          || !["LONG", "SHORT"].includes(body.direction) || !EVENT_EVALUATION_TIMEFRAMES.includes(body.timeframe)
          || !validReferenceSlots(body.slots)) return Promise.reject(invalidResponse());
      return request(`${REFERENCE_BASE_PATH}/${encodeURIComponent(id)}/versions`, {
        method: "PUT", body, referenceRequest: true, referenceConflict: true,
        transformResponse: (payload) => payload?.pack, validateResponse: validReferencePack,
      });
    },

    saveReferencePackStatus(id, body) {
      if (typeof id !== "string" || !REFERENCE_ID.test(id) || !isRevision(body?.expectedRevision)
          || typeof body.enabled !== "boolean"
          || !(body.activeVersion === null || isBoundedInteger(body.activeVersion, 1))) return Promise.reject(invalidResponse());
      return request(`${REFERENCE_BASE_PATH}/${encodeURIComponent(id)}/status`, {
        method: "PUT", body, referenceRequest: true, referenceConflict: true,
        transformResponse: (payload) => payload?.pack, validateResponse: validReferencePack,
      });
    },

    retireReferencePackVersion(id, body) {
      if (typeof id !== "string" || !REFERENCE_ID.test(id) || !isRevision(body?.expectedRevision)
          || !isBoundedInteger(body.version, 1)) return Promise.reject(invalidResponse());
      return request(`${REFERENCE_BASE_PATH}/${encodeURIComponent(id)}/retire`, {
        method: "POST", body, referenceRequest: true, referenceConflict: true,
        transformResponse: (payload) => payload?.pack, validateResponse: validReferencePack,
      });
    },

    getSyncStatus() {
      return request(`${ADMIN_BASE_PATH}/sync-status`, {
        validateResponse: (payload) => hasExactKeys(payload, DOCUMENT_KEYS)
          && DOCUMENT_KEYS.every((key) => validSyncEntry(payload[key])),
      });
    },

    getHealth() {
      return request(`${ADMIN_BASE_PATH}/health`, { validateResponse: validHealth });
    },

    getAudit() {
      return request(`${ADMIN_BASE_PATH}/audit`, {
        validateResponse: (payload) => Array.isArray(payload)
          && payload.length <= 100
          && payload.every(validAuditEntry),
      });
    },

    getPatternHistory() {
      return request(`${ADMIN_BASE_PATH}/history`);
    },

    getPatternResearch() {
      return request(`${ADMIN_BASE_PATH}/pattern-research`);
    },

    getUsers() {
      return request(`${ADMIN_BASE_PATH}/users`);
    },

    getReferrals() {
      return request(`${ADMIN_BASE_PATH}/referrals`);
    },

    getPayouts() {
      return request(`${ADMIN_BASE_PATH}/payouts`);
    },

    submitCommand(command) {
      return routeJavaCommand(command, { scope: "ADMIN", legacy: async body => {
        const result = await request(`${ADMIN_BASE_PATH}/commands`, { method: "POST", body });
        return waitForLegacyCommand({ ...result, commandId: body.commandId },
          () => request(`${ADMIN_BASE_PATH}/commands/${encodeURIComponent(body.commandId)}`), body.expiresAt);
      } });
    },

    async createIdentityLookup(publicUserId) {
      const result = await routeJavaCommand({ type: "IDENTITY_LOOKUP", payload: { targetPublicUserId: publicUserId } }, { scope: "ADMIN", legacy: () => request(`${ADMIN_BASE_PATH}/users/${encodeURIComponent(publicUserId)}/identity-lookups`, {
        method: "POST", body: {},
      }) });
      if (!result.acknowledgement) return result;
      if (!result.acknowledgement.accepted) throw new AdminApiError(503, result.acknowledgement.code, result.acknowledgement.message);
      if (!result.acknowledgement.result?.identity) throw new AdminApiError(410, "IDENTITY_RESULT_EXPIRED", "This one-time identity result expired. Load the identity again.");
      return { status: "ACCEPTED", identity: result.acknowledgement.result?.identity };
    },

    readIdentityLookup(lookupId) {
      return request(`${ADMIN_BASE_PATH}/identity-lookups/${encodeURIComponent(lookupId)}`);
    },
  };
}

export const adminApi = createAdminApi();
